import { Injectable, inject } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { from } from 'rxjs'
import { map, switchMap, tap } from 'rxjs/operators'
import { PromoTextApiService, PromoTextResponse } from 'src/api/promo-text'
import { ToasterService } from 'src/shared/toaster/toaster.service'
import { catchAndLog } from 'src/shared/utils/catch-and-log'
import { archivePromoText, archivePromoTextFailure, archivePromoTextSuccess, createPromoText, createPromoTextFailure, createPromoTextSuccess, loadPromoTexts, loadPromoTextsFailure, loadPromoTextsSuccess, updatePromoText, updatePromoTextFailure, updatePromoTextSuccess } from './promo-text.actions'

function unwrapPromoText(response: PromoTextResponse) {
  if (response?.error || !response?.data) throw response?.error ?? new Error('Empty promo text response')
  return response.data
}

@Injectable()
export class PromoTextEffects {
  private actions$ = inject(Actions)
  private promoTextApi = inject(PromoTextApiService)
  private toaster = inject(ToasterService)


  loadPromoTexts$ = createEffect(() => this.actions$.pipe(
    ofType(loadPromoTexts),
    switchMap(() =>
      from(this.promoTextApi.search({ archived: false }, 0, 500)).pipe(
        map(response => loadPromoTextsSuccess({ promoTexts: response.data?.data ?? [] })),
        catchAndLog(this.toaster, 'Unable to fetch promo texts', loadPromoTextsFailure)))))

  createPromoText$ = createEffect(() => this.actions$.pipe(
    ofType(createPromoText),
    tap(() => this.toaster.progress('Saving promo text...')),
    switchMap(action =>
      from(this.promoTextApi.create(action.promoText)).pipe(
        map(response => createPromoTextSuccess({ promoText: unwrapPromoText(response) })),
        tap(() => this.toaster.success('Promo text saved successfully.')),
        catchAndLog(this.toaster, 'Unable to save promo text', createPromoTextFailure)))))

  updatePromoText$ = createEffect(() => this.actions$.pipe(
    ofType(updatePromoText),
    tap(() => this.toaster.progress('Saving promo text...')),
    switchMap(action =>
      from(this.promoTextApi.update(action.id, action.promoText)).pipe(
        map(response => updatePromoTextSuccess({ promoText: unwrapPromoText(response) })),
        tap(() => this.toaster.success('Promo text saved successfully.')),
        catchAndLog(this.toaster, 'Unable to save promo text', updatePromoTextFailure)))))

  archivePromoText$ = createEffect(() => this.actions$.pipe(
    ofType(archivePromoText),
    tap(() => this.toaster.progress('Archiving promo text...')),
    switchMap(action =>
      from(this.promoTextApi.delete(action.id)).pipe(
        map(() => archivePromoTextSuccess({ id: action.id })),
        tap(() => this.toaster.success('Promo text archived successfully.')),
        catchAndLog(this.toaster, 'Unable to archive promo text', archivePromoTextFailure)))))
}